/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { reorderArray } from '../utils';
import { getAllKeyValuesForMultipleQuestions, KeyValuesSet } from './question_utils';
import { DocumentVersionType, LaneType, PageTypeEnum, PageType, SelectedButtonInfo, LinkSlugType } from './types';
import { walkPage } from './walkPage';

// Swimlane helper

/**
 * This function walks from the root node through all the children node, recursively, to find
 * all the questions that can lead to the given pageId or pageName.
 *
 * If it has found a path (or paths), as the stack unrolls it adds the keys to the keysFound.
 *
 * It returns all the question keys that can lead to that page.
 */
export function findKeys(
  pageId: string | undefined,
  pageName: string | undefined,
  pages: {
    [id: string]: PageType;
  },
  nameToPageId: {
    [name: string]: string;
  },
  swimlane_page_name: string,
  linksVisited = new Set<string>(), // To prevent infinite recursion incase of loops
  keysFound = new KeyValuesSet()
): KeyValuesSet | undefined {
  if (pageName && pageName === swimlane_page_name) {
    // Note keysFound will be empty at this stage - we are returning 'truthy'
    // and let the tail return add the keys
    return keysFound;
  }
  if (!pageId) return undefined;

  const page = pages[pageId];
  if (!page) return undefined;
  let foundTargetPageId = false;

  const processLink = (link: LinkSlugType) => {
    if (linksVisited.has(link?.link)) {
      if (pageName && pageName === swimlane_page_name) foundTargetPageId = true; // I think can't happen
    } else {
      linksVisited.add(link?.link);
      if (
        // Note we need to make a copy of the linksVisited, to catch multiple paths to the swimlane
        findKeys(
          nameToPageId[link?.link],
          link?.link,
          pages,
          nameToPageId,
          swimlane_page_name,
          new Set(linksVisited),
          keysFound
        )
      ) {
        foundTargetPageId = true;
      }
    }
    return foundTargetPageId;
  };

  walkPage(page, (link) => processLink(link));
  if (foundTargetPageId) {
    if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
      // If we have a found a path to our swimlane by going through this page's next_link, then add all the options in this page
      getAllKeyValuesForMultipleQuestions(page, keysFound);
      if (page.pageType === PageTypeEnum.CALCULATOR) {
        page.formulaBins?.forEach((bin) => {
          keysFound.add(`bin_${page.name.toUpperCase()}`, bin.name, `${page.name} Calculator`, bin.name);
        });
      }
    }
    return keysFound;
  }
  return undefined;
}

/**
 * This function walks from the root node through all the children node, recursively, to find
 * all the questions that can lead to the given pageId or pageName.
 *
 * If it has found a path (or paths), as the stack unrolls it adds the keys to the keysFound.
 *
 * It returns all the question keys that can lead to that page.
 */
export function findKeysForDoc(
  documentVersion: DocumentVersionType,
  target_page_name: string
): KeyValuesSet | undefined {
  return findKeys(
    documentVersion.algorithmRootPageId,
    undefined,
    documentVersion.pages,
    documentVersion.nameToPageId,
    target_page_name
  );
}

/**
 * Return whether this lane ONLY (i.e. ignoring mergeLeft) matches the selectedKeys
 */
export const laneMatchesUnmerged = (
  lane: LaneType,
  selectedKeys: { [key: string]: { value: SelectedButtonInfo['value'] } }
) => {
  if (!lane) return false;
  const choices = lane.choices;
  return Object.keys(choices).every(
    (key) =>
      selectedKeys[key] &&
      (selectedKeys[key]!.value === choices[key] || (choices[key] === 'NO' && selectedKeys[key]!.value == null))
  );
};

/** For the given lane, if it is mergeLeft then it returns false.
 *  Otherwise it looks at that lane and any merged lanes to its right, and returns true if any of those lanes
 *  match the selectedKeys.
 */
export function laneMatches(
  lanes: LaneType[],
  laneIndex: number,
  selectedKeys: { [key: string]: { value: SelectedButtonInfo['value'] } },
  recursed = false
): boolean {
  const lane = lanes![laneIndex];
  if (!lane) return false;
  if (!recursed && lane.mergeLeft) return false; // Ignore, because we will have already considered this lane when doing the left lane
  if (recursed && !lane.mergeLeft) return false; // Ignore, because when recursing only consider merge left lanes
  if (laneMatchesUnmerged(lane, selectedKeys)) return true;
  if (laneMatches(lanes, laneIndex + 1, selectedKeys, true)) {
    return true;
  }
  return false;
}

/** For the given lane, returns true if it's a "Rittman" lane, i.e. has no conditions
 */
export function laneIsEmpty(lanes: LaneType[], laneIndex: number, recursed = false): boolean {
  const lane = lanes![laneIndex];
  if (!lane) return true;
  if (!recursed && lane.mergeLeft) return false; // Ignore, because we will have already considered this lane when doing the left lane
  if (recursed && !lane.mergeLeft) return false; // Ignore, because when recursing only consider merge left lanes

  if (Object.keys(lane.choices).length !== 0) {
    return false;
  }
  return laneIsEmpty(lanes, laneIndex + 1, true);
}

export const valueMatchToYesNo = (value1: string | undefined, value2: string | true | undefined) => {
  if (value1 == null || value2 == null) return undefined;
  if (value1 === value2) return 'YES';
  if (value1 === 'YES' && value2 === true) return 'YES';
  if (value1 !== 'NO' && value2 === true) return undefined;
  return 'NO';
};

export function valueOfMergedLane(lanes: LaneType[], laneIndex: number, rowKey: string, rowValue: string | true) {
  while (lanes?.[laneIndex + 1]?.mergeLeft) laneIndex++;
  let foundNo = false;
  while (true) {
    if (laneIndex < 0) return undefined;
    const yesNo = valueMatchToYesNo(lanes?.[laneIndex]?.choices[rowKey], rowValue);
    if (yesNo === 'YES') return 'YES';
    if (yesNo === 'NO') foundNo = true;
    if (!lanes?.[laneIndex]?.mergeLeft) {
      return foundNo ? 'NO' : undefined;
    }
    laneIndex--;
  }
}

export function leftMostMergedLane(lanes: LaneType[] | undefined, laneIndex: number | undefined): LaneType | undefined {
  if (laneIndex == null) return;
  while (true) {
    const lane = lanes?.[laneIndex];
    if (!lane) return; // should never happen
    if (!lane.mergeLeft) return lane;
    laneIndex--;
  }
}

function shouldMergeLeftWhenReorderingLanes(lanes: LaneType[], oldIndex: number, newIndex: number): boolean {
  const isSameGroup =
    newIndex < oldIndex
      ? lanes.slice(newIndex + 1, oldIndex + 1).every((lane) => lane.mergeLeft)
      : lanes.slice(oldIndex + 1, newIndex + 1).every((lane) => lane.mergeLeft);
  if (isSameGroup) {
    if (newIndex < oldIndex) {
      if (!lanes[newIndex]?.mergeLeft) {
        // We are moving a lane to basically swap positions with its current leader, so swap their emrge left
        lanes[newIndex] = { ...lanes[newIndex]!, mergeLeft: true };
        return false;
      }
    }
    return true;
  }
  // When moving outside the group, set merge left to whatever the lane to the right of its new position
  return lanes[newIndex < oldIndex ? newIndex : newIndex + 1]?.mergeLeft || false;
}

export function reorderLanes(lanes_: LaneType[], oldIndex: number, newIndex: number): LaneType[] {
  if (oldIndex === newIndex) return lanes_;
  const lanes = [...lanes_];
  const newMergeLeft = shouldMergeLeftWhenReorderingLanes(lanes, oldIndex, newIndex);
  if (!lanes[oldIndex]?.mergeLeft && lanes[oldIndex + 1]?.mergeLeft) {
    // If we are not merge left, but the lane next to us is, then turn off merge left for that lane
    lanes[oldIndex + 1] = { ...lanes[oldIndex + 1]!, mergeLeft: false };
  }
  // See if the newIndex and oldIndex are in the same group.  They are in the same group if all lanes between them (include last lane) are mergeLeft
  if (newMergeLeft !== lanes[oldIndex]?.mergeLeft) {
    lanes[oldIndex] = { ...lanes[oldIndex]!, mergeLeft: newMergeLeft };
  }
  return reorderArray(lanes, oldIndex, newIndex);
}

export function mapIndexToLaneGroupNum(lanes: LaneType[] | undefined): number[] {
  if (!lanes) return [];
  // Map from lane index to the group number. Say we have lanes with indexes like this:
  // | 0 | 1 | 2 3 | 4 5 6 | 7 | 8 | 9 |
  // Where | is a lane without mergeLeft, then the group numbers would be:
  // | 1 | 2 | 3 3 | 4 4 4 | 5 | 6 | 7 |
  let x = 1;
  return lanes?.map((lane, index, array) => {
    if (lane.mergeLeft || index === 0) return x;
    x += 1;
    return x;
  });
}
